import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import ContactModal from './ContactModal';

const { FiPlus, FiSearch, FiFilter, FiEdit2, FiTrash2, FiMail, FiPhone, FiBriefcase } = FiIcons;

const Contacts = () => {
  const { contacts, companies, deleteContact, contactTags } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.company || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag === 'All' || contact.tags?.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      deleteContact(id);
    }
  };

  const handleAddNew = () => {
    setEditingContact(null);
    setIsModalOpen(true);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Contacts</h1>
        <button 
          onClick={handleAddNew}
          className="bg-blue-600 text-white px-8 py-4 rounded-3xl font-black shadow-xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 transition-all"
        >
          <SafeIcon icon={FiPlus} /> Add Contact
        </button>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
          <input 
            placeholder="Search contacts..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium"
          />
        </div>
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiFilter} className="text-slate-400" />
          <select 
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            className="bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 outline-none font-bold text-sm text-slate-600"
          >
            <option value="All">All Tags</option>
            {contactTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {filteredContacts.map((contact) => {
            const linkedCompany = companies.find(c => c.id === contact.companyId);
            return (
              <motion.div 
                key={contact.id}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 hover:shadow-xl transition-all group"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={contact.avatarUrl || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'} 
                      alt={contact.name}
                      className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow-md"
                    />
                    <div>
                      <h3 className="font-black text-slate-900 text-lg">{contact.name}</h3>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        {linkedCompany?.name || contact.company || 'Private Contact'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEdit(contact)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button onClick={() => handleDelete(contact.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm text-slate-500 font-medium">
                    <SafeIcon icon={FiMail} className="text-blue-500" />
                    {contact.email || 'No email'}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500 font-medium">
                    <SafeIcon icon={FiPhone} className="text-blue-500" />
                    {contact.phone || 'No phone'}
                  </div>
                  {linkedCompany && (
                    <div className="flex items-center gap-3 text-sm text-slate-500 font-medium">
                      <SafeIcon icon={FiBriefcase} className="text-blue-500" />
                      {linkedCompany.name}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1">
                  {contact.tags?.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-[9px] font-black rounded-full uppercase tracking-widest bg-blue-50 text-blue-600">
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setEditingContact(null); }} 
        contact={editingContact} 
      />
    </motion.div>
  );
};

export default Contacts;